import { useQuery } from '@tanstack/vue-query'
import { hcaClientFactory } from '@/api/factories/HcaClientFactory'
import { whenAuthenticatedAnd } from '@/utils/query-utils'
import { PlantGroupWithGeneratorHierarchyDto } from '@/api/generated/HCA'
import { SelectOptions } from '@/components/form-inputs/common/select-option'
import { usePermissions } from '@/composables/usePermissions'
import { computed } from 'vue'

export type GeneratorFlatDto = {
    generator: {
        id: string
        functionalLocation?: string | null
        plantSid: number
        plantGroupSid: number
        bookmarkId?: number | null
        generatorName: string
    }
    plant: {
        sid: number
        name: string
        plantGroupSid: number
    }
    plantGroup: {
        sid: number
        name: string
    }
}

export const useGeneratorsHierarchyQuery = () => {
    const { userPermissions } = usePermissions()

    return useQuery({
        queryKey: ['generatorsHierarchy'],
        queryFn: async ({ signal }) => {
            const {
                data: { result },
            } = await hcaClientFactory.masterDataApi.getGenerators(undefined, { signal })
            return result
        },
        enabled: whenAuthenticatedAnd(computed(() => !!userPermissions.value.canViewGeneratorsData)),
        staleTime: Infinity,
    })
}

export const flattenGeneratorsHierarchy = (hierarchyStructure: PlantGroupWithGeneratorHierarchyDto[]): GeneratorFlatDto[] => {
    const flattenedGenerators: GeneratorFlatDto[] = []

    hierarchyStructure.forEach((plantGroup) => {
        const plantGroupData = {
            sid: plantGroup.sid ?? 0,
            name: plantGroup.name ?? '-',
        }

        plantGroup.plants?.forEach((plant) => {
            const plantData = {
                sid: plant.sid ?? 0,
                name: plant.name ?? '-',
                plantGroupSid: plantGroup.sid ?? 0,
            }

            plant.generators?.forEach((generator) => {
                flattenedGenerators.push({
                    generator: {
                        id: generator.generatorSid ?? '',
                        functionalLocation: generator.functionalLocation,
                        plantGroupSid: plantGroup.sid ?? 0,
                        plantSid: plant.sid ?? 0,
                        generatorName: generator.generatorName ?? '',
                        bookmarkId: generator.bookmarkId,
                    },
                    plantGroup: plantGroupData,
                    plant: plantData,
                })
            })
        })
    })

    return flattenedGenerators
}

export const generatorOptions = (hierarchyStructure: GeneratorFlatDto[]): SelectOptions => {
    return hierarchyStructure.map((e) => ({
        label:
            !e.generator.functionalLocation || e.generator.id === e.generator.functionalLocation
                ? `${e.generator.id} / - /${e.generator.generatorName}`
                : `${e.generator.id} / ${e.generator.functionalLocation} / ${e.generator.generatorName}`,
        value: e.generator.id,
    }))
}
